/**
 * Modal Stack - singleton-aware z-index/ESC/focus orchestration for every
 * dialog surface in the app.
 *
 * Why this exists (PM 2026-06-10 bug pass):
 *
 *   - Bug E: end-game "Sign in to share" CTA used to open the Login Modal
 *     BEHIND the still-open end-game overlay (no z-index ordering).
 *   - Bug F: end-game overlay had no backdrop/Esc/X-button dismissal.
 *   - Bug H: tapping "Sign in" twice mounted two Login Modals; closing the
 *     end-game then left orphaned stacks. Auth needed to be a singleton.
 *   - Bug K: bugs reproduced on every game because each game owns a copy of
 *     the wireup. Fix has to live in shared infra, not per-game.
 *
 * Architecture:
 *
 *   - Every long-lived surface (modal, sheet, end-game overlay) calls
 *     `registerModal()` on mount and `entry.release()` on tear-down.
 *   - The stack assigns an explicit `z-index` to the backdrop based on the
 *     surface's `tier`. Higher-tier surfaces always paint above lower-tier
 *     ones (Auth above Result, etc).
 *   - A single document-level `keydown` listener owns Escape - it routes
 *     the key to the topmost stack entry whose `dismissOnEsc` is true.
 *     Individual shells no longer attach their own ESC listeners.
 *   - Backdrop click is still wired locally in each shell, but they read
 *     `dismissOnBackdrop` from the entry so the policy is per-surface.
 *   - Singleton support: callers may pass `singletonKey`. If a key is
 *     already taken, `registerModal` returns the existing entry and moves
 *     focus to it - the caller does NOT mount a duplicate.
 *   - Focus restore: each entry captures `document.activeElement` on mount
 *     and restores it on release.
 *
 * Public surface (used by both the components package and consumers):
 *
 *   - `registerModal(opts) -> ModalStackEntry`
 *   - `entry.release()` - returns DOM to its pre-mount state and pops the
 *     stack.
 *   - `entry.requestClose()` - invokes the caller's onClose handler. The
 *     caller is responsible for calling `release()` inside that handler.
 *   - `getTopEntry()` - useful for tests + future "view result" pill work.
 *
 * NB: this module never touches game state. It is pure UI scaffolding.
 */

export type ModalTier = 'result' | 'sheet' | 'modal' | 'auth';

const TIER_Z_INDEX: Record<ModalTier, number> = {
  // Result modals (end-game overlay) sit above page chrome but below
  // any modal that the user opens FROM the overlay (eg the Login Modal
  // launched via "Sign in with Nostr to share").
  result: 1100,
  // Bottom sheets (How to Play) sit at the same visual tier as a modal
  // when invoked from gameplay - they cover the board but are not auth.
  sheet: 1150,
  // Regular modals (Share, debug overlays) sit above the sheet tier.
  modal: 1200,
  // Auth (Login Modal) sits at the very top so it always paints above
  // whatever launched it.
  auth: 1300,
};

export interface RegisterModalOpts {
  /** The full backdrop element. The stack writes inline z-index onto this. */
  element: HTMLElement;
  /** Tier slot. Determines z-index ordering. */
  tier: ModalTier;
  /**
   * Called when the stack's global ESC handler decides this entry should
   * close. Callers should invoke their own teardown logic + `release()`.
   */
  onClose: () => void;
  /** When false, the global ESC handler skips this entry. Default true. */
  dismissOnEsc?: boolean;
  /**
   * Advisory flag stored on the entry. Consumed by per-shell backdrop
   * click handlers. The stack itself does NOT bind a backdrop listener;
   * shells must check `entry.dismissOnBackdrop` when wiring their own.
   * Default true.
   */
  dismissOnBackdrop?: boolean;
  /**
   * Singleton key. If supplied and the key is already taken, registration
   * returns the existing entry (and focuses it). The caller MUST inspect
   * `entry.isExisting` and skip mounting a duplicate.
   */
  singletonKey?: string;
}

export interface ModalStackEntry {
  readonly id: string;
  readonly tier: ModalTier;
  readonly element: HTMLElement;
  readonly singletonKey?: string;
  readonly dismissOnBackdrop: boolean;
  /**
   * True when `registerModal` returned a pre-existing singleton instead of
   * registering a fresh entry. The caller MUST NOT mount a second surface
   * when this is true.
   */
  readonly isExisting: boolean;
  /** Pop the entry off the stack and restore focus. Idempotent. */
  release: () => void;
  /** Invoke the caller's `onClose`. Used by the global ESC handler + tests. */
  requestClose: () => void;
  /** Move focus to the first focusable element inside `element`. */
  focus: () => void;
}

interface InternalEntry {
  id: string;
  tier: ModalTier;
  element: HTMLElement;
  onClose: () => void;
  dismissOnEsc: boolean;
  dismissOnBackdrop: boolean;
  singletonKey?: string;
  prevFocus: Element | null;
  released: boolean;
}

const stack: InternalEntry[] = [];
const singletons = new Map<string, InternalEntry>();
let nextId = 1;
let keydownAttached = false;

function attachKeydownOnce(): void {
  if (keydownAttached) return;
  keydownAttached = true;
  document.addEventListener('keydown', onGlobalKeydown, true);
}

function onGlobalKeydown(e: KeyboardEvent): void {
  if (e.key !== 'Escape') return;
  const top = stack[stack.length - 1];
  if (!top) return;
  if (!top.dismissOnEsc) return;
  e.stopPropagation();
  e.preventDefault();
  top.onClose();
}

function applyTierZ(entry: InternalEntry): void {
  // Use the tier base; layer entries within the same tier by their stack
  // position so a second result-tier modal would paint above the first.
  const sameTierIndex = stack.filter((s) => s.tier === entry.tier).length - 1;
  const base = TIER_Z_INDEX[entry.tier];
  entry.element.style.zIndex = String(base + sameTierIndex);
}

function focusFirstFocusable(root: HTMLElement): void {
  const focusables = root.querySelectorAll<HTMLElement>(
    [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(','),
  );
  // Prefer the first non-close button so we don't auto-focus the X.
  for (const el of focusables) {
    if (!el.classList.contains('gv-modal-close')) {
      el.focus();
      return;
    }
  }
  focusables[0]?.focus();
}

export function registerModal(opts: RegisterModalOpts): ModalStackEntry {
  attachKeydownOnce();

  if (opts.singletonKey) {
    const existing = singletons.get(opts.singletonKey);
    if (existing && !existing.released) {
      // Move focus into the existing surface so the user sees it react.
      focusFirstFocusable(existing.element);
      return toPublic(existing, true);
    }
  }

  const id = `gv-modal-${nextId++}`;
  const internal: InternalEntry = {
    id,
    tier: opts.tier,
    element: opts.element,
    onClose: opts.onClose,
    dismissOnEsc: opts.dismissOnEsc ?? true,
    dismissOnBackdrop: opts.dismissOnBackdrop ?? true,
    ...(opts.singletonKey !== undefined ? { singletonKey: opts.singletonKey } : {}),
    prevFocus: document.activeElement,
    released: false,
  };

  stack.push(internal);
  if (opts.singletonKey) singletons.set(opts.singletonKey, internal);
  applyTierZ(internal);

  // Defer focus to next microtask so callers can wire their own initial
  // focus (eg the nsec input autofocus in login-modal.ts).
  queueMicrotask(() => {
    if (!internal.released) focusFirstFocusable(internal.element);
  });

  return toPublic(internal, false);
}

function toPublic(internal: InternalEntry, isExisting: boolean): ModalStackEntry {
  return {
    id: internal.id,
    tier: internal.tier,
    element: internal.element,
    ...(internal.singletonKey !== undefined ? { singletonKey: internal.singletonKey } : {}),
    dismissOnBackdrop: internal.dismissOnBackdrop,
    isExisting,
    release: (): void => {
      if (internal.released) return;
      internal.released = true;
      const idx = stack.indexOf(internal);
      if (idx >= 0) stack.splice(idx, 1);
      if (internal.singletonKey) singletons.delete(internal.singletonKey);
      // Restore z-indices on remaining same-tier entries so a closed top
      // doesn't leave a gap.
      for (const e of stack) applyTierZ(e);
      // Restore focus.
      const prev = internal.prevFocus;
      if (prev instanceof HTMLElement && document.contains(prev)) {
        prev.focus();
      }
    },
    requestClose: (): void => {
      internal.onClose();
    },
    focus: (): void => {
      focusFirstFocusable(internal.element);
    },
  };
}

/** Read-only accessor used by tests + the "View result" pill follow-up. */
export function getTopEntry(): ModalStackEntry | null {
  const top = stack[stack.length - 1];
  if (!top) return null;
  return toPublic(top, true);
}

/** Test helper: drain the stack. Not exported from package index. */
export function __resetModalStackForTests(): void {
  for (const e of stack.slice()) {
    e.released = true;
    if (e.singletonKey) singletons.delete(e.singletonKey);
  }
  stack.length = 0;
  singletons.clear();
}
